import {QueryReturnValue} from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import {FetchBaseQueryError} from '@reduxjs/toolkit/query';

import {
  ActivityFetchParamsType,
  ActivityLocationParamsType,
  ActivityType,
} from 'app/activity/types/activity';
import {sweetAPI} from 'app/store/sweet-api';
import {providesTagsByURL, showMessages} from 'app/store/sweet-api/utils';
import {taskDispatcher} from 'app/subscriptions/taskDispatcher';
import {ACTIVITY_API_URL, EXPORT_ACTIVITY_API_URL} from 'app/urls';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';
import {ACTIVITY_RTK_TAGS} from 'app/utils/rtk-tags';

import {ListResponseType, UserJobSerializedType} from 'sweet-types';

import type {DateRangeType} from 'app/general/dates/date-range';

export const extendedAPI = sweetAPI
  .enhanceEndpoints({addTagTypes: [ACTIVITY_RTK_TAGS.TAGS]})
  .injectEndpoints({
    endpoints: (build) => ({
      getObjectActivity: build.query<
        ListResponseType<ActivityType>,
        ActivityFetchParamsType
      >({
        query: (params) => ({
          url: ACTIVITY_API_URL,
          params,
        }),
        onQueryStarted: showMessages({
          onError: () => gt("Couldn't fetch document activity"),
        }),
        providesTags: providesTagsByURL(ACTIVITY_RTK_TAGS.TAGS),
      }),
      exportDocumentActivity: build.mutation<
        {result: string},
        {
          contentType: string;
          objectId: number | string;
          range: DateRangeType;
        }
      >({
        queryFn: (
          {objectId, contentType, range},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<{result: string}, FetchBaseQueryError, any>
        > => {
          return api
            .dispatch(
              taskDispatcher(
                (
                  baseQuery({
                    url: EXPORT_ACTIVITY_API_URL,
                    method: 'POST',
                    body: {
                      object_id: objectId,
                      content_type: contentType,
                      datetime_start: range.start,
                      datetime_end: range.end,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .then(
              (job) => ({data: job}),
              () => Promise.reject(gt("Couldn't export document activity"))
            );
        },
      }),
    }),
  });

// https://redux-toolkit.js.org/rtk-query/usage/queries#hook-types
export const {
  useGetObjectActivityQuery: useObjectActivity,
  useExportDocumentActivityMutation: useExportDocumentActivity,
} = extendedAPI;

export const convertLocationParamsToFetchParams = (
  query: ActivityLocationParamsType
): ActivityFetchParamsType => ({
  page: query.page != null ? query.page : '1',
  page_size: query.page_size != null ? query.page_size : DEFAULT_PAGE_SIZE,
  actor: query.actor,
  target_content_type: query.target_content_type,
  target_object_id: query.target_object_id,
});
